import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { useLanguage } from "@/i18n";
import { useT } from "@/i18n_extra3";
import { BLOG_POSTS } from "@/data/blogPosts";

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  useEffect(() => {
    if (post) {
      document.title = `${post.title[lang] || post.title.en} | ClearRoute Auto Glass`;
      return () => {
        document.title = "ClearRoute Auto Glass — Windshield Repair & Replacement | Peabody, MA · Greater Boston";
      };
    }
  }, [post, lang]);

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex flex-col items-center justify-center gap-6 text-black/60">
        <p>404 — post not found</p>
        <Link to="/blog" className="text-[#B8860B] hover:underline">Back to blog</Link>
      </div>
    );
  }

  const paragraphs = post.body[lang] || post.body.en;

  return (
    <div className="App bg-[#FAF9F6] text-[#111111] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 pt-40 pb-24 max-w-3xl mx-auto px-5 sm:px-8 w-full" data-testid="blog-post">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-black/50 hover:text-[#B8860B] transition-colors" data-testid="blog-back-link">
          <ArrowLeft className="w-4 h-4" /> Blog
        </Link>
        <span className="block mt-10 text-[11px] font-bold tracking-widest uppercase text-[#B8860B]">{post.readMin} {t.blogSection.min}</span>
        <h1 className="mt-4 font-display font-bold tracking-tight text-4xl sm:text-5xl leading-[1.1]">{post.title[lang] || post.title.en}</h1>
        <div className="mt-10 space-y-6">
          {paragraphs.map((p, i) => (
            <p key={i} className={`leading-relaxed ${i === 0 ? "text-xl text-black/75 font-medium" : "text-lg text-black/65"}`}>
              {p}
            </p>
          ))}
        </div>
        <div className="mt-14 bg-[#111111] rounded-3xl p-10 text-center">
          <h2 className="font-display font-bold text-2xl text-[#F8F8F8]">{t.quote.title}</h2>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "#quote" } })}
            data-testid="blog-quote-cta"
            className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#E6C965] text-black font-bold px-8 py-4 rounded-full transition-colors"
          >
            {t.hero.ctaPrimary} <ArrowUpRight className="w-5 h-5" />
          </button>
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
